import { CourseAccordionSkeleton } from '../../../components/program/CourseAccordionSkeleton';
import { CurriculumError } from '../../../components/program/CurriculumError';
import { InteractiveStructure } from '../../../components/program/InteractiveStructure';
import { useProgramCurriculum } from '../../../data/useProgramCurriculum';
import { InformativeStructure } from './InformativeStructure';

interface Props {
  programId: string;
  /**
   * The learner's sandId once they are enrolled, null before. It is the axis
   * this component branches on: with it, Savanna resolves the flow for a
   * person and the tree becomes something to work through; without it, the
   * tree describes the curriculum to someone deciding whether to join.
   */
  sandId: string | null;
  /** Whether a live subscription currently unlocks the paid content. */
  covered: boolean;
  onSubscribe: () => void;
  /** Shown instead of the structure when there is no structure to show. */
  description: string;
}

/**
 * The "Program Structure" section.
 *
 * Two trees behind one heading, chosen by enrolment rather than by coverage:
 *
 * - INFORMATIVE, not enrolled. One course fetched, blocks as leaves with a
 *   Free / Partially Free / Paid badge. The question being answered is "what
 *   is in this and what does it cost".
 * - INTERACTIVE, enrolled. Every course, each fetched on expand, activities
 *   as leaves linking into Savanna. The question is "where do I go next".
 *
 * Coverage does not pick the tree, only what the chosen one shows: an enrolled
 * learner without a subscription gets the interactive tree with locks on the
 * paid activities and the unlock bar beneath, which is the upsell.
 *
 * Both read `savannaCoursesForProgram` through `useProgramCurriculum`, so the
 * list of courses costs one request either way. The informative tree also uses
 * its first-curriculum read; the interactive tree ignores that and fetches per
 * course instead.
 */
export const ProgramStructure = ({
  programId,
  sandId,
  covered,
  onSubscribe,
  description,
}: Props) => {
  const { locations, course, loading, failed } = useProgramCurriculum(
    programId,
    sandId
  );

  /*
   * The heading is rendered in every state, loading included. It is the one
   * part of this section that does not depend on the request, and holding it
   * back only makes the page jump when the courses arrive.
   */
  const body = () => {
    if (loading) {
      // Three cards, the low end of a GSA program, so the section settles
      // rather than growing when the real list lands.
      return (
        <>
          <CourseAccordionSkeleton />
          <CourseAccordionSkeleton />
          <CourseAccordionSkeleton />
        </>
      );
    }

    // Checked BEFORE the empty case. An empty `locations` after a failure
    // means "we could not ask", not "there is nothing here", and the
    // description below would state the second.
    if (failed) {
      return <CurriculumError subject="this program's structure" />;
    }

    // A program with no Savanna-backed course at all, which is what this page
    // fell back to for every program before the curricula were reachable.
    if (locations.length === 0) {
      return <p className="text-muted-foreground">{description}</p>;
    }

    return sandId ? (
      <InteractiveStructure
        programId={programId}
        sandId={sandId}
        covered={covered}
        onSubscribe={onSubscribe}
      />
    ) : (
      <InformativeStructure
        locations={locations}
        course={course}
        covered={covered}
        onSubscribe={onSubscribe}
      />
    );
  };

  return (
    <section>
      <h2 className="text-2xl font-semibold text-heading">Program Structure</h2>
      <div className="mt-6 flex flex-col gap-4">{body()}</div>
    </section>
  );
};
